import type { Writable } from "svelte/store";
import type { LinkboxConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// Linkbox does not answer other origins. Calls go through /api/linkbox. The
// API token, the email and the password of the account are request headers.
// Linkbox cannot rename, and cannot store empty files.

class FileSystemLinkbox extends FS {
  constructor(config: LinkboxConfig) {
    super(config);
  }

  get linkboxConfig(): LinkboxConfig {
    return this.config as LinkboxConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await linkboxCall(this.linkboxConfig, mkURL("/api/linkbox/list", "dir", dirPath));
      if (!Array.isArray(js)) {
        throw new Error("linkbox list failed");
      }
      return FsDir.fromWire(dirPath, js);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the API key. Fetch the file
    // and hand the bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = mkURL("/api/linkbox/download", "path", path);
    let xhr = await linkboxSend("GET", url, this.linkboxConfig, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = mkURL("/api/linkbox/upload", "path", path);
    await linkboxSend("PUT", url, this.linkboxConfig, file, httpProgress);
  }

  async rename(_path: string, _newPath: string): Promise<void> {
    throw new Error("Linkbox cannot rename files");
  }

  async createDirectory(dirPath: string): Promise<void> {
    await linkboxCall(this.linkboxConfig, mkURL("/api/linkbox/mkdir", "path", dirPath), "POST");
  }

  // A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    await linkboxCall(this.linkboxConfig, mkURL("/api/linkbox/delete", "path", path), "POST");
  }
}

// The email and the password are percent-encoded: a header cannot carry
// every character.
export function linkboxHeaders(config: LinkboxConfig): Record<string, string> {
  return {
    "X-Linkbox-Token": config.token,
    "X-Linkbox-Email": encodeURIComponent(config.email),
    "X-Linkbox-Password": encodeURIComponent(config.password),
  };
}

export async function testLinkboxConnection(config: LinkboxConfig): Promise<void> {
  await linkboxCall(config, "/api/linkbox/test");
}

async function linkboxCall(config: LinkboxConfig, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, {
    method,
    headers: linkboxHeaders(config),
  });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `linkbox ${rsp.status}`);
  }
  return js;
}

function linkboxSend(
  method: string,
  url: string,
  config: LinkboxConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(linkboxHeaders(config))) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      linkboxErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`linkbox ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("linkbox request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function linkboxErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `linkbox ${xhr.status}`;
}

export function makeFileSystemLinkbox(config: LinkboxConfig): FileSystemLinkbox {
  return new FileSystemLinkbox(config);
}
